import styleConfirmOtp from "./confirmOtp.module.css";
import logo from "../../../assets/img/logo.png";
import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import LoadingSpinner from "../../../components/Loading/LoadingSpinner";
import Footer from "../../../layouts/footer/Footer";
import {
  verifyMobileConfirmOtp,
  verifyMobileSendOtp,
  loginAfterVerification,
  validateEmail,
} from "../../../adapters/apis/auth/auth";
import Cookies from "js-cookie";
import UseAuth from "../../../hooks/UseAuth";

function ConfirmOtp() {
  const [otp, setOtp] = useState(["", "", "", "", "", ""]); // 6 digits
  const [isLoading, setIsLoading] = useState(false);
  const inputsRef = useRef([]);
  const [timeLeft, setTimeLeft] = useState(0);
  const [submissionError, setSubmissionError] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const { setAuth } = UseAuth();

  const navigate = useNavigate();
  const location = useLocation();

  // ---- Robust token sourcing ----
  const token =
    location.state?.token || // best UX
    sessionStorage.getItem("request_code") || // survives refresh
    new URLSearchParams(window.location.search).get("token") || // deep link
    "";

  const user_id = sessionStorage.getItem("user_id");
  const mobile = sessionStorage.getItem("mobile");
  const context = sessionStorage.getItem("context");

  const getSmsExpireSeconds = () => {
    const v = Number(localStorage.getItem("sms_otp_expire"));
    return Number.isFinite(v) && v > 0 ? v : 300;
  };

  const contextRedirect = {
    register: "/login",
    login_verify: "/login",
  };

  // Focus the first input safely
  const focusFirstInput = () => {
    const el = inputsRef.current?.[0];
    if (el && typeof el.focus === "function") {
      el.focus();
    }
  };

  useEffect(() => {
    // Wait a tick to ensure the input is in the DOM (StrictMode safe)
    const id = setTimeout(focusFirstInput, 0);
    return () => clearTimeout(id);
  }, []);

  // for refreshing page
  useEffect(() => {
    if (token) sessionStorage.setItem("request_code", token);
  }, [token]);

  // Arabic countdown message (kept as-is; optional to generalize)
  const formatCountdownMessage = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    if (minutes < 1) {
      return "تبقى دقيقة";
    } else if (minutes <= 2) {
      return "تبقى دقيقتان";
    } else if (minutes <= 3) {
      return `تبقى ثلاث دقائق`;
    } else if (minutes <= 4) {
      return `تبقى أربع دقائق`;
    } else if (minutes <= 5) {
      return `تبقى خمس دقائق`;
    }
    return "";
  };

  // ---- Countdown that survives refresh (per-token) ----
  useEffect(() => {
    // Guard: nothing to do without a token
    if (!token) return;

    // Guard: sessionStorage might not exist in SSR / blocked environments
    if (typeof window === "undefined" || !window.sessionStorage) return;

    const STORAGE_KEY = `otp_end_time:${token}`;

    // Read or initialize end time using dynamic expiry (seconds from sessionStorage)
    const now = Date.now();
    let endTime = Number(localStorage.getItem(STORAGE_KEY));
    if (!Number.isFinite(endTime) || endTime <= now) {
      const expireSeconds = getSmsExpireSeconds(); // 🔸 dynamic seconds
      endTime = now + expireSeconds * 1000; // 🔸 use dynamic here
      localStorage.setItem(STORAGE_KEY, String(endTime));
    }

    // Helper to compute remaining seconds
    const calcRemaining = () =>
      Math.max(0, Math.floor((endTime - Date.now()) / 1000));

    // Initialize immediately
    setTimeLeft(calcRemaining());

    // Interval ticker
    const intervalId = window.setInterval(() => {
      const remaining = calcRemaining();
      setTimeLeft(remaining);
      if (remaining <= 0) {
        window.clearInterval(intervalId);
        // sessionStorage.removeItem(STORAGE_KEY); // optional
      }
    }, 1000);

    const handleVisibility = () => setTimeLeft(calcRemaining());
    document.addEventListener("visibilitychange", handleVisibility);

    // Cleanup on unmount or token change
    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [token]);

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s < 10 ? "0" : ""}${s}`;
  };

  // Handle input change
  const handleChange = (index, value) => {
    if (!/^\d*$/.test(value)) return; // Only numbers

    const newOtp = [...otp];
    newOtp[index] = value.slice(-1); // One digit
    setOtp(newOtp);

    if (value && index < 5) {
      inputsRef.current[index + 1].focus();
    }
  };

  // Handle backspace: move to previous if empty
  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputsRef.current[index - 1].focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault(); // Prevent default paste behavior

    const pastedText = e.clipboardData.getData("text").trim();

    // Check if it's exactly 6 digits
    if (/^\d{6}$/.test(pastedText)) {
      const digits = pastedText.split("");
      setOtp(digits); // Fill all inputs

      // Focus the last input after paste
      inputsRef.current[5]?.focus();
    }
  };

  const handleResend = () => {
    // Clear current OTP
    setOtp(["", "", "", "", "", ""]);
    setSubmissionError("");
    // Focus first input
    inputsRef.current[0]?.focus();

    try {
      setIsLoading(true);
      verifyMobileSendOtp({ mobile: mobile, context: context })
        .then((response) => {
          setIsLoading(false);
          const result = response?.data;
          if (result.code === 0) {
            const expireSeconds =
              Number(result?.data?.sms_otp_expire) ||
              Number(localStorage.getItem("sms_otp_expire")) ||
              300;

            // Update countdown immediately based on dynamic expiry
            const newEndTime = Date.now() + expireSeconds * 1000; // 🔸 dynamic
            const STORAGE_KEY = `otp_end_time:${result.data.request_code || token}`;
            localStorage.setItem(STORAGE_KEY, String(newEndTime));
            setTimeLeft(
              Math.max(0, Math.floor((newEndTime - Date.now()) / 1000)),
            );

            setSubmissionError("");
            setOtp(["", "", "", "", "", ""]);

            const request_code = result.data.request_code;
            sessionStorage.setItem("context", result.data.context);
            sessionStorage.setItem("mobile", result.data.mobile);
            sessionStorage.setItem("request_code", request_code);
            sessionStorage.setItem("user_id", result.data.user_id);
            navigate(`/confirm-otp?token=${encodeURIComponent(request_code)}`, {
              state: { request_code },
            });
          } else {
            const errorMsg = result.message;
            setSubmissionError(errorMsg);
          }
        })
        .catch((err) => {
          //const errorMsg = err?.response?.data?.message || "حدث خطأ غير متوقع";
          const errorMsg =
            err?.response?.data?.message ||
            err?.response?.data?.error?.reason ||
            "حدث خطأ غير متوقع";
          setSubmissionError(errorMsg);
          setIsLoading(false);
        });
    } catch (err) {
      setIsLoading(false);
      setSubmissionError("حدث خطأ أثناء إرسال الرمز");
    }
  };

  const handleVerify = () => {
    //  Check if all 6 digits are filled
    const code = otp.join("");
    if (code.length !== 6) {
      setSubmissionError("يجب ادخال رمز التحقق OTP");
      return;
    }

    // All checks passed → proceed to API call
    setIsLoading(true);
    setSubmissionError("");
    const payload = {
      user_id: user_id,
      mobile: mobile,
      token: token, // session token from register
      otp: code, // the 6-digit code
      context: context,
    };
    verifyMobileConfirmOtp(payload)
      .then((response) => {
        const result = response?.data;
        if (result.code === 0) {
          const email = sessionStorage.getItem("email");
          if (email) {
            validateEmail({ email: email })
              .then((res) => {
                navigate(`/mail-verify?email=${email}`);
              })
              .catch((error) => {
                setErrorMsg(error.response.data.message);
                setIsLoading(false);
              });
          } else {
            const password = sessionStorage.getItem("pass");
            loginAfterVerification({ mobile: mobile, password: password })
              .then((response) => {
                // If Loggedin
                sessionStorage.removeItem("pass");
                const accessToken = response?.data?.access_token;
                const refreshToken = response?.data?.refresh_token;
                const full_name = response?.data?.first_name;
                const phone_verified = response?.data?.phone_verified;
                const ocr_verified = response?.data?.ocr_verified;
                // set  data in local storage
                Cookies.set("auth-token", accessToken);
                Cookies.set("refresh-token", refreshToken);
                Cookies.set("user-name", full_name);
                // Cookies.set("phone_verified", phone_verified);
                // Cookies.set("ocr_verified", ocr_verified);

                //for support-page
                localStorage.setItem("user-full-name", full_name);

                const isAuth = true;
                // set context
                // setAuth({ full_name, accessToken, isAuth , phone_verified: response?.data?.phone_verified, ocr_verified: response?.data?.ocr_verified });
                setAuth({
                  full_name,
                  accessToken,
                  isAuth,
                  phone_verified,
                  ocr_verified,
                });
                navigate("/", { replace: true });
              })
              .catch((err) => {
                setIsLoading(false);
                setSubmissionError(err?.response?.data?.message || "حدث خطأ");
              });
          }
        }
      })
      .catch((err) => {
        setIsLoading(false);
        setSubmissionError(err?.response?.data?.message || "حدث خطأ");
      });
  };

  if (!token) {
    return (
      <>
        <section className={styleConfirmOtp["otp-section"]}>
          <div className="container text-center mt-5">
            <h3 className="text-danger">
              لا يمكن الوصول إلى هذه الصفحة مباشرة
            </h3>
            <p>يرجى البدء من صفحة التسجيل</p>
            <button
              className="btn btn-primary mt-3"
              onClick={() => navigate("/register")}
            >
              العودة للتسجيل
            </button>
          </div>
        </section>
        <Footer />
      </>
    );
  }

  return (
    <>
      {isLoading ? (
        <LoadingSpinner />
      ) : (
        <section className={styleConfirmOtp["otp-section"]}>
          <div className="container mb-5">
            <div className={`card ${styleConfirmOtp["card"]}`}>
              <a href="/" className={styleConfirmOtp["logo-wrap"]}>
                <img src={logo} alt="Logo" />
              </a>
              {errorMsg && (
                <div
                  className="alert alert-danger  mt-3"
                  id="backend_error"
                  role="alert"
                >
                  <li className="">{errorMsg}</li>

                  {}
                </div>
              )}
              <div className={`card-body ${styleConfirmOtp["card-body"]}`}>
                <h5
                  className={`${styleConfirmOtp["card-title"]} card-title fw-bold`}
                >
                  التحقق من الهوية
                </h5>
                <p
                  className={styleConfirmOtp["subtitle"]}
                  style={{ fontSize: "18px" }}
                >
                  جاري التحقق من بيانات هوية المستخدم
                </p>
                <p className={styleConfirmOtp["subtitle"]}>
                  لقد تم بالفعل إرسال رمز التحقق OTP إلى رقم هاتفك المسجل، يرجى
                  التحقق من صندوق الرسائل الخاص بك
                </p>

                {/* OTP inputs */}
                <div
                  className={styleConfirmOtp["otp-inputs"]}
                  onPaste={handlePaste}
                  role="group"
                  aria-label="OTP inputs"
                >
                  {otp.map((digit, index) => (
                    <input
                      key={index}
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handleChange(index, e.target.value)}
                      onKeyDown={(e) => handleKeyDown(index, e)}
                      ref={(el) => (inputsRef.current[index] = el)}
                      className={`${styleConfirmOtp["otp-input"]} ${
                        digit ? styleConfirmOtp["filled"] : ""
                      }`}
                      aria-label={`الرقم ${index + 1}`}
                    />
                  ))}
                </div>

                {/* Timer + Resend */}
                <div className={styleConfirmOtp["timer-row"]}>
                  {timeLeft > 0 && (
                    <>
                      <span className={styleConfirmOtp["time-left"]}>
                        {formatTime(timeLeft)}
                      </span>

                      <span className={styleConfirmOtp["time-left"]}>
                        {formatCountdownMessage(timeLeft)}
                      </span>
                    </>
                  )}
                  {submissionError && (
                    <p
                      className="text-danger text-center mt-3 fw-bold"
                      dir="rtl"
                    >
                      {submissionError}
                    </p>
                  )}
                </div>

                {/* ConfirmOtp */}

                <div className={styleConfirmOtp["actions"]}>
                  <button
                    type="button"
                    className={styleConfirmOtp["btn-verify"]}
                    disabled={isLoading}
                    onClick={handleVerify}
                  >
                    التحقق من رمز الOTP
                  </button>

                  <button
                    type="button"
                    className={styleConfirmOtp["resend-link"]}
                    onClick={handleResend}
                    disabled={isLoading}
                  >
                    إعادة إرسال رمز التحقق
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}
      <Footer />
    </>
  );
}

export default ConfirmOtp;
